// src/components/AiQuickMatch.tsx
import React, { useState, useRef, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, TextField, MenuItem, Button,
    Stack, IconButton, Avatar, useTheme, LinearProgress
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import WorkIcon from '@mui/icons-material/Work';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MinorCrashIcon from '@mui/icons-material/MinorCrash';

import { encodeId } from '../utils/idEncoder';

// const AI_ENGINE_URL = 'http://127.0.0.1:8000/ai-search';

const AI_ENGINE_URL = 'https://api.workhorse.com.sg/ai/ai-search';

interface VehicleUI {
    match_score: number | null;
    price_rating: string | null;
    is_best_match: boolean;
    pills: string[];
}

interface Vehicle {
    system_id: string;
    vehicle_id: number;
    condition: string;
    brand_model: string;
    body_type: string;
    transmission: string;
    fuel: string;
    price: number;
    image_url: string;
    description: string;
    ui: VehicleUI;
}

interface Message {
    role: 'ai' | 'user';
    content: string;
}

const AiQuickMatch = () => {
    const theme = useTheme();
    const chatEndRef = useRef<HTMLDivElement>(null);

    const [selectedCondition, setSelectedCondition] = useState('Any Condition');
    const [selectedType, setSelectedType] = useState('Truck');
    const [selectedBudget, setSelectedBudget] = useState('Under $60,000');
    const [selectedUsage, setSelectedUsage] = useState('Logistics');

    const [chatInput, setChatInput] = useState('');
    const [messages, setMessages] = useState<Message[]>([
        { role: 'ai', content: "Great! Tell me your needs, or use the quick match options on the left. I'll find the best matches for you." }
    ]);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<Vehicle[]>([]);
    const [searchCount, setSearchCount] = useState<number | null>(null);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    const formatPrice = (price: number) => {
        return price > 0 ? `$${price.toLocaleString()}` : 'POA';
    };

    const performSearch = async (promptText: string) => {
        if (!promptText.trim()) return;

        const updatedMessages = [...messages, { role: 'user' as const, content: promptText }];
        setMessages(updatedMessages);
        setLoading(true);
        setResults([]);

        const historyPayload = updatedMessages.map(m => ({
            role: m.role === 'ai' ? 'assistant' : m.role,
            content: m.content || ''
        }));

        try {
            const response = await fetch(AI_ENGINE_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ messages: historyPayload })
            });

            if (!response.ok) throw new Error('Failed to connect to AI engine');

            const data = await response.json();
            
            if (data.is_conversational) {
                 setMessages(prev => [...prev, { role: 'ai', content: data.bot_reply }]);
            } else {
                setResults(data.matched_vehicles || []);
                setSearchCount(data.results_count);
                setMessages(prev => [...prev, { role: 'ai', content: data.bot_reply }]);
            }

        } catch (error) {
            console.error(error);
            setMessages(prev => [...prev, { role: 'ai', content: '⚠️ Sorry, my AI engine is currently unreachable.' }]);
        } finally {
            setLoading(false);
        }
    };

    const handleDropdownSubmit = () => {
        const conditionText = selectedCondition !== 'Any Condition' ? selectedCondition : '';
        const prompt = `I need a ${conditionText} ${selectedType} ${selectedBudget} for ${selectedUsage}.`;
        performSearch(prompt);
    };

    const handleChatSubmit = () => {
        performSearch(chatInput);
        setChatInput('');
    };

    const handleCardClick = (vehicleId: number, condition: string) => {
        const encryptedId = encodeId(vehicleId);
        const routePrefix = condition === 'New' ? '/new-vehicle-details' : '/used-vehicle-details';
        window.open(`${routePrefix}/${encryptedId}`, '_blank');
    };

    return (
        <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 4 } }}>
            <Paper elevation={0} sx={{ 
                p: 0, borderRadius: 4, border: '1px solid #eee', overflow: 'hidden', mb: 4, boxShadow: '0 10px 40px -10px rgba(0,0,0,0.05)'
            }}>
                <Grid container>
                    {/* LEFT SIDE: QUICK MATCH */}
                    <Grid size={{ xs: 12, md: 7}} sx={{ p: 4, borderRight: { md: '1px solid #eee' } }}>
                        <Stack direction="row" alignItems="center" spacing={2} mb={1}>
                            <Avatar sx={{ bgcolor: '#FF8E00', width: 40, height: 40 }}>
                                <AutoAwesomeIcon />
                            </Avatar>
                            <Typography variant="h4" fontWeight="800">Quick Match</Typography>
                        </Stack>
                        <Typography color="text.secondary" mb={4} ml={7}>
                            Tell us your needs, we'll do the rest.
                        </Typography>

                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={4} sx={{ position: 'relative' }}>
                            <Box sx={{ position: 'absolute', top: 20, left: 40, right: 40, height: 2, bgcolor: '#eef2ff', zIndex: 0 }} />
                            {[
                                { icon: <LocalShippingIcon fontSize="small" />, title: "1. Type", desc: "Choose vehicle type" },
                                { icon: <AccountBalanceWalletIcon fontSize="small" />, title: "2. Budget", desc: "Select your budget", color: '#FF8E00' },
                                { icon: <WorkIcon fontSize="small" />, title: "3. Usage", desc: "Tell us your use case", color: '#3b82f6' },
                                { icon: <SearchIcon fontSize="small" />, title: "4. Matches", desc: "AI finds best options", color: '#8b5cf6' }
                            ].map((step, idx) => (
                                <Box key={idx} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 1, width: '25%' }}>
                                    <Avatar sx={{ width: 40, height: 40, bgcolor: '#f8fafc', color: step.color || '#64748b', border: `2px solid ${step.color || '#e2e8f0'}`, mb: 1 }}>
                                        {step.icon}
                                    </Avatar>
                                    <Typography variant="caption" fontWeight="bold">{step.title}</Typography>
                                    <Typography variant="caption" color="text.secondary" align="center" sx={{ fontSize: '0.65rem' }}>{step.desc}</Typography>
                                </Box>
                            ))}
                        </Stack>

                        <Grid container spacing={2} mb={3}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    select fullWidth size="small"
                                    value={selectedCondition} onChange={(e) => setSelectedCondition(e.target.value)}
                                    InputProps={{ startAdornment: <MinorCrashIcon color="action" sx={{ mr: 1, fontSize: 18 }} /> }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                >
                                    {['Any Condition', 'New', 'Used'].map(opt => (
                                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    select fullWidth size="small"
                                    value={selectedType} onChange={(e) => setSelectedType(e.target.value)}
                                    InputProps={{ startAdornment: <LocalShippingIcon color="action" sx={{ mr: 1, fontSize: 18 }} /> }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                >
                                    {['Truck', 'Van', 'Pickup', 'Any Type'].map(opt => (
                                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    select fullWidth size="small"
                                    value={selectedBudget} onChange={(e) => setSelectedBudget(e.target.value)}
                                    InputProps={{ startAdornment: <Typography color="success.main" fontWeight="bold" sx={{ mr: 1 }}>$</Typography> }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                >
                                    {['Any Budget', 'Under $30,000', 'Under $60,000', '$60,000 - $100,000', 'Over $100,000'].map(opt => (
                                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    select fullWidth size="small"
                                    value={selectedUsage} onChange={(e) => setSelectedUsage(e.target.value)}
                                    InputProps={{ startAdornment: <WorkIcon color="warning" sx={{ mr: 1, fontSize: 18 }} /> }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                >
                                    {['Logistics', 'Delivery', 'Construction', 'Catering', 'Passenger Transport'].map(opt => (
                                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </Grid>

                        <Box textAlign="right">
                            <Button 
                                variant="contained" size="large" onClick={handleDropdownSubmit} disabled={loading}
                                sx={{ bgcolor: '#FF8E00', '&:hover': { bgcolor: '#e68000' }, borderRadius: 2, px: 4, fontWeight: 'bold' }}
                            >
                                Find My Matches
                            </Button>
                        </Box>
                    </Grid>

                    {/* RIGHT SIDE: CHAT INTERFACE */}
                    <Grid size={{ xs: 12, md: 5 }} sx={{ bgcolor: '#fafafa', display: 'flex', flexDirection: 'column', height: '400px' }}>
                        <Box sx={{ p: 2, borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'white' }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <AutoAwesomeIcon sx={{ color: '#FF8E00' }} />
                                <Typography fontWeight="bold">WorkHorse AI</Typography>
                            </Stack>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#22c55e' }} />
                                <Typography variant="caption" color="text.secondary">Online</Typography>
                            </Stack>
                        </Box>

                        <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {messages.map((msg, idx) => (
                                <Box key={idx} sx={{ alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', display: 'flex', gap: 1 }}>
                                    {msg.role === 'ai' && (
                                        <Avatar sx={{ width: 28, height: 28, bgcolor: 'white', border: '1px solid #FF8E00', color: '#FF8E00' }}>
                                            <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                                        </Avatar>
                                    )}
                                    <Paper elevation={0} sx={{ 
                                        p: 1.5, borderRadius: 3,
                                        borderTopRightRadius: msg.role === 'user' ? 4 : 12,
                                        borderTopLeftRadius: msg.role === 'ai' ? 4 : 12,
                                        bgcolor: msg.role === 'user' ? '#FF8E00' : '#fffef8',
                                        color: msg.role === 'user' ? 'white' : 'text.primary',
                                        border: msg.role === 'ai' ? '1px solid #ffe4b5' : 'none'
                                    }}>
                                        <Typography variant="body2">{msg.content}</Typography>
                                    </Paper>
                                </Box>
                            ))}

                            {loading && (
                                <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #eee', width: '85%' }}>
                                    <Stack direction="row" justifyContent="space-between" mb={1}>
                                        <Typography variant="caption" color="text.secondary">Searching database...</Typography>
                                        <Typography variant="caption" fontWeight="bold">80%</Typography>
                                    </Stack>
                                    <LinearProgress color="success" variant="indeterminate" sx={{ height: 6, borderRadius: 3 }} />
                                </Paper>
                            )}
                            <div ref={chatEndRef} />
                        </Box>

                        <Box sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid #eee' }}>
                            <TextField
                                fullWidth placeholder="Type your message..." variant="outlined" size="small"
                                value={chatInput} onChange={(e) => setChatInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleChatSubmit()}
                                InputProps={{
                                    endAdornment: (
                                        <Stack direction="row" spacing={0.5}>
                                            <IconButton size="small"><AttachFileIcon fontSize="small" /></IconButton>
                                            <IconButton 
                                                onClick={handleChatSubmit} disabled={loading || !chatInput.trim()}
                                                sx={{ bgcolor: '#FF8E00', color: 'white', '&:hover': { bgcolor: '#e68000' } }}
                                            >
                                                <SendIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Stack>
                                    ),
                                    sx: { borderRadius: 8, bgcolor: '#f8fafc' }
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {results.length > 0 && (
                <Box>
                    <Stack spacing={2}>
                        {results.map((v, idx) => (
                            <Paper 
                                key={idx} elevation={0} onClick={() => handleCardClick(v.vehicle_id, v.condition)}
                                sx={{ 
                                    p: 2, borderRadius: 3, border: '1px solid #eee', display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' }, gap: 3, position: 'relative',
                                    overflow: 'hidden', cursor: 'pointer', transition: 'all 0.2s ease',
                                    '&:hover': { borderColor: '#FF8E00', boxShadow: '0 4px 15px rgba(255,142,0,0.15)', transform: 'translateY(-2px)' }
                                }}
                            >
                                {v.ui.is_best_match && (
                                    <Box sx={{ position: 'absolute', top: 0, left: 0, bgcolor: '#FFc107', color: '#000', fontSize: '0.65rem', fontWeight: 'bold', px: 1.5, py: 0.5, borderBottomRightRadius: 8, zIndex: 1 }}>
                                        BEST MATCH
                                    </Box>
                                )}

                                <Box sx={{ width: { xs: '100%', sm: 200 }, height: 140, borderRadius: 2, overflow: 'hidden', flexShrink: 0 }}>
                                    <img src={v.image_url || 'https://via.placeholder.com/300x200?text=No+Image'} alt={v.brand_model} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                </Box>

                                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                    <Typography variant="h6" fontWeight="800" sx={{ mb: 0.5 }}>{v.brand_model}</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                        {v.condition === 'New' ? 'New' : 'Used'} • {v.fuel} • {v.transmission}
                                    </Typography>
                                    <Stack direction="row" spacing={2} mb={2} flexWrap="wrap">
                                        {v.ui.pills.map((pill, pIdx) => (
                                            <Stack key={pIdx} direction="row" alignItems="center" spacing={0.5}>
                                                <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                                                <Typography variant="caption" color="text.secondary" fontWeight="500">{pill}</Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                    <Stack direction="row" alignItems="baseline" spacing={2}>
                                        <Typography variant="h5" fontWeight="900">{formatPrice(v.price)}</Typography>
                                        {v.ui.price_rating && (
                                            <Typography variant="subtitle2" sx={{ color: v.ui.price_rating === 'Great Price' ? '#22c55e' : '#f59e0b', fontWeight: 'bold' }}>
                                                {v.ui.price_rating}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Box>

                                {v.ui.match_score && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: 100, borderLeft: { sm: '1px solid #eee' }, pl: { sm: 3 } }}>
                                        <Box sx={{ width: 60, height: 60, borderRadius: '50%', border: `4px solid ${v.ui.match_score > 90 ? '#22c55e' : '#84cc16'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Typography variant="h6" fontWeight="bold" sx={{ color: v.ui.match_score > 90 ? '#22c55e' : '#84cc16' }}>
                                                {v.ui.match_score}%
                                            </Typography>
                                        </Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>Match Score</Typography>
                                    </Box>
                                )}
                            </Paper>
                        ))}
                    </Stack>
                </Box>
            )}
        </Box>
    );
};

export default AiQuickMatch;